// Adaptive palette driven by heating_power (0-100).
// Bands:
//   idle  : 0
//   low   : 1-25
//   medium: 26-60
//   high  : 61-100
//
// 5% deadband at boundaries to prevent flicker when power oscillates.

export type Band = 'idle' | 'low' | 'medium' | 'high';

export interface PaletteTokens {
  bgGradient: string;
  ink: string;
  tempGradient: string;
  glow: string;
  barGradient: string;
  btnGradient: string;
  btnShadow: string;
}

const PALETTES: Record<Band, PaletteTokens> = {
  high: {
    bgGradient: 'linear-gradient(165deg, #ffd0c0 0%, #ff7a4a 55%, #d92510 100%)',
    ink: '#4a0d04',
    tempGradient: 'linear-gradient(180deg, #4a0d04 0%, #c01a08 100%)',
    glow: 'radial-gradient(circle, rgba(220,50,15,0.65), rgba(220,50,15,0) 65%)',
    barGradient: 'linear-gradient(90deg, #ff5a3e, #c01a08)',
    btnGradient: 'linear-gradient(180deg, #ff5a3e, #c01a08)',
    btnShadow: '0 8px 22px rgba(192,26,8,0.35)',
  },
  medium: {
    bgGradient: 'linear-gradient(165deg, #fff4e6 0%, #ffd9b8 55%, #ffb88a 100%)',
    ink: '#4a2410',
    tempGradient: 'linear-gradient(180deg, #4a2410 0%, #d35a1a 100%)',
    glow: 'radial-gradient(circle, rgba(255,140,60,0.55), rgba(255,140,60,0) 65%)',
    barGradient: 'linear-gradient(90deg, #ff8a4c, #e55f1c)',
    btnGradient: 'linear-gradient(180deg, #ff8a4c, #e55f1c)',
    btnShadow: '0 8px 22px rgba(229,95,28,0.35)',
  },
  low: {
    bgGradient: 'linear-gradient(165deg, #fdf8f2 0%, #f9e6d3 55%, #f3d2b3 100%)',
    ink: '#5a3a22',
    tempGradient: 'linear-gradient(180deg, #5a3a22 0%, #b9824a 100%)',
    glow: 'radial-gradient(circle, rgba(230,160,100,0.35), rgba(230,160,100,0) 65%)',
    barGradient: 'linear-gradient(90deg, #ffaa7a, #d97a3a)',
    btnGradient: 'linear-gradient(180deg, #ffaa7a, #d97a3a)',
    btnShadow: '0 8px 22px rgba(217,122,58,0.30)',
  },
  idle: {
    bgGradient: 'linear-gradient(165deg, #eef4fb 0%, #d8e5f1 55%, #b9cde2 100%)',
    ink: '#1f3a5c',
    tempGradient: 'linear-gradient(180deg, #1f3a5c 0%, #4a78a8 100%)',
    glow: 'radial-gradient(circle, rgba(120,160,210,0.4), rgba(120,160,210,0) 65%)',
    barGradient: 'linear-gradient(90deg, #5a8ec0, #2e5a8a)',
    btnGradient: 'linear-gradient(180deg, #5a8ec0, #2e5a8a)',
    btnShadow: '0 8px 22px rgba(46,90,138,0.30)',
  },
};

export function tokensFor(band: Band): PaletteTokens {
  return PALETTES[band];
}

const DEADBAND = 5;

const BOUNDARY_OF: Partial<Record<Band, number>> = {
  low: 1,
  medium: 26,
  high: 61,
};

function bandFromPower(power: number): Band {
  if (power >= 61) return 'high';
  if (power >= 26) return 'medium';
  if (power >= 1) return 'low';
  return 'idle';
}

const ORDER: Record<Band, number> = { idle: 0, low: 1, medium: 2, high: 3 };

export function nextBand(prev: Band, power: number | null | undefined): Band {
  if (power === null || power === undefined) return 'low';
  if (power <= 0) return 'idle';

  const raw = bandFromPower(power);
  if (raw === prev) return prev;

  if (ORDER[raw] > ORDER[prev]) {
    // Going up — only step up when at least DEADBAND above the boundary
    // of the band we're entering, so values hovering at 26 or 61 don't
    // flip-flop between adjacent bands.
    const enteringBoundary = BOUNDARY_OF[raw];
    if (enteringBoundary == null) return raw;
    if (power >= enteringBoundary + DEADBAND) return raw;
    return prev;
  }

  // Going down — only step down when at least DEADBAND below the boundary
  // that brought us into the current band.
  const currentBoundary = BOUNDARY_OF[prev];
  if (currentBoundary == null) return raw;
  if (power <= currentBoundary - DEADBAND) return raw;
  return prev;
}
